/**
 * WHAT: CRUD interface for managing connections to MDM/Data Governance tools.
 * WHY: DAMA PRO imports classification and catalog data from external MDM systems
 *      (Ataccama, Collibra, Purview); this component lets admins configure, test,
 *      and sync those connections.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../../config/api.config';

/**
 * WHAT: Full CRUD component for MDM connections with test, sync, and auto-sync support.
 * WHY: Each MDM connection brings external classification data into DAMA; admins must
 *      be able to create, configure credentials, test connectivity, and trigger syncs.
 */
const ProConnections = () => {
    const [connections, setConnections] = useState([]);
    const [availableTypes, setAvailableTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedConnection, setSelectedConnection] = useState(null);
    const [formData, setFormData] = useState({
        connection_name: '',
        connection_type: 'MANUAL',
        description: '',
        base_url: '',
        api_version: '',
        auth_type: 'API_KEY',
        vault_path: '',
        auto_sync_enabled: false,
        sync_schedule: ''
    });
    const [testResult, setTestResult] = useState(null);
    const [syncing, setSyncing] = useState(null);

    useEffect(() => {
        fetchConnections();
    }, []);

    /* WHAT: Loads all MDM connections and available connection types from the backend.
     * WHY: Populates the connections table and the type dropdown in the create form. */
    const fetchConnections = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_CONFIG.BASE_URL}/api/pro/connections/`);
            setConnections(response.data.connections || []);
            setAvailableTypes(response.data.available_types || ['MANUAL', 'ATACCAMA', 'COLLIBRA']);
        } catch (err) {
            console.error('Error fetching connections:', err);
            setError('Failed to load connections');
        } finally {
            setLoading(false);
        }
    };

    /* WHAT: Resets the form to defaults and opens the create-connection modal.
     * WHY: Ensures the modal always starts with a clean slate for new connections. */
    const handleCreate = () => {
        setModalMode('create');
        setFormData({
            connection_name: '',
            connection_type: 'MANUAL',
            description: '',
            base_url: '',
            api_version: '',
            auth_type: 'API_KEY',
            vault_path: '',
            auto_sync_enabled: false,
            sync_schedule: ''
        });
        setTestResult(null);
        setShowModal(true);
    };

    /* WHAT: Fetches full connection details and opens the edit modal with pre-filled data.
     * WHY: The list view only has summary fields; editing requires fetching the full
     *      record (including auth config) from the detail endpoint. */
    const handleEdit = async (connection) => {
        setModalMode('edit');
        setSelectedConnection(connection);
        try {
            const response = await axios.get(`${API_CONFIG.BASE_URL}/api/pro/connections/${connection.connection_id}/`);
            setFormData({
                connection_name: response.data.connection_name || '',
                connection_type: response.data.connection_type || 'MANUAL',
                description: response.data.description || '',
                base_url: response.data.base_url || '',
                api_version: response.data.api_version || '',
                auth_type: response.data.auth_type || 'API_KEY',
                vault_path: response.data.vault_path || '',
                auto_sync_enabled: response.data.auto_sync_enabled || false,
                sync_schedule: response.data.sync_schedule || ''
            });
            setTestResult(null);
            setShowModal(true);
        } catch (err) {
            alert('Failed to load connection details');
        }
    };

    /* WHAT: Submits the connection form to create or update a connection.
     * WHY: A single handler covers both create (POST) and edit (PUT) flows,
     *      keeping the modal logic unified. */
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (modalMode === 'create') {
                await axios.post(`${API_CONFIG.BASE_URL}/api/pro/connections/`, formData);
            } else {
                await axios.put(`${API_CONFIG.BASE_URL}/api/pro/connections/${selectedConnection.connection_id}/`, formData);
            }
            setShowModal(false);
            fetchConnections();
        } catch (err) {
            alert('Failed to save connection: ' + (err.response?.data?.error || err.message));
        }
    };

    /* WHAT: Deletes a connection after browser confirmation.
     * WHY: Removing a connection also removes its sync history; the confirm dialog
     *      prevents accidental deletion. */
    const handleDelete = async (connectionId) => {
        if (!window.confirm('Are you sure you want to delete this connection?')) return;
        try {
            await axios.delete(`${API_CONFIG.BASE_URL}/api/pro/connections/${connectionId}/`);
            fetchConnections();
        } catch (err) {
            alert('Failed to delete connection');
        }
    };

    /* WHAT: Sends a connectivity test request for a specific MDM connection.
     * WHY: Admins need to verify credentials and network access are correct
     *      before relying on a connection for catalog sync. */
    const handleTest = async (connectionId) => {
        setTestResult({ loading: true, connectionId });
        try {
            const response = await axios.post(`${API_CONFIG.BASE_URL}/api/pro/connections/${connectionId}/test/`);
            setTestResult({
                connectionId,
                success: response.data.success,
                message: response.data.message
            });
            fetchConnections();
        } catch (err) {
            setTestResult({
                connectionId,
                success: false,
                message: err.response?.data?.error || 'Test failed'
            });
        }
    };

    /* WHAT: Triggers a full catalog sync from the MDM tool into DAMA's data model.
     * WHY: Syncing imports classification and asset metadata so PRO scopes and
     *      sensitivity scores stay up to date with the external governance platform. */
    const handleSync = async (connectionId) => {
        setSyncing(connectionId);
        try {
            const response = await axios.post(`${API_CONFIG.BASE_URL}/api/pro/connections/${connectionId}/sync/`, {
                sync_type: 'FULL_CATALOG'
            });
            alert(`Sync completed: ${response.data.assets_synced} assets, ${response.data.classifications_synced} classifications`);
            fetchConnections();
        } catch (err) {
            alert('Sync failed: ' + (err.response?.data?.error || err.message));
        } finally {
            setSyncing(null);
        }
    };

    /* WHAT: Maps a connection status to its Bootstrap badge color class.
     * WHY: Color-coded status badges (green=CONNECTED, red=FAILED) let admins
     *      quickly spot unhealthy connections in the table. */
    const getStatusBadge = (status) => {
        const badges = {
            'CONNECTED': 'bg-success',
            'FAILED': 'bg-danger',
            'PENDING': 'bg-warning text-dark'
        };
        return badges[status] || 'bg-secondary';
    };

    /* WHAT: Maps a connection type to its Bootstrap badge color class.
     * WHY: Distinct colors per MDM vendor (blue=ATACCAMA, cyan=COLLIBRA, etc.)
     *      help admins visually distinguish connection types in the table. */
    const getTypeBadge = (type) => {
        const badges = {
            'ATACCAMA': 'bg-primary',
            'COLLIBRA': 'bg-info',
            'PURVIEW': 'bg-purple',
            'ALATION': 'bg-warning text-dark',
            'MANUAL': 'bg-secondary'
        };
        return badges[type] || 'bg-secondary';
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="mb-1">MDM Connections</h4>
                    <p className="text-muted mb-0">
                        Configure connections to MDM/Data Governance tools
                    </p>
                </div>
                <button className="btn btn-primary" onClick={handleCreate}>
                    <i className="fas fa-plus me-2"></i>
                    New Connection
                </button>
            </div>

            {error && (
                <div className="alert alert-danger">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {error}
                </div>
            )}

            {/* Connections Table */}
            <div className="card">
                <div className="card-body p-0">
                    <table className="table table-hover mb-0">
                        <thead className="table-dark">
                            <tr>
                                <th>Connection Name</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Last Sync</th>
                                <th>Auto Sync</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {connections.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-4 text-muted">
                                        No connections configured. Click "New Connection" to add one.
                                    </td>
                                </tr>
                            ) : (
                                connections.map(conn => (
                                    <tr key={conn.connection_id}>
                                        <td>
                                            <strong>{conn.connection_name}</strong>
                                            {conn.description && (
                                                <div className="small text-muted">{conn.description}</div>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`badge ${getTypeBadge(conn.connection_type)}`}>
                                                {conn.connection_type}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`badge ${getStatusBadge(conn.connection_status)}`}>
                                                {conn.connection_status}
                                            </span>
                                            {testResult && testResult.connectionId === conn.connection_id && !testResult.loading && (
                                                <div className={`small mt-1 ${testResult.success ? 'text-success' : 'text-danger'}`}>
                                                    {testResult.message}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {conn.last_sync_at ? (
                                                <div>
                                                    <small>{new Date(conn.last_sync_at).toLocaleString()}</small>
                                                    <div className="small text-muted">
                                                        {conn.last_sync_status}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-muted">Never</span>
                                            )}
                                        </td>
                                        <td>
                                            {conn.auto_sync_enabled ? (
                                                <span className="badge bg-success">Enabled</span>
                                            ) : (
                                                <span className="badge bg-secondary">Disabled</span>
                                            )}
                                        </td>
                                        <td className="text-end">
                                            <div className="btn-group btn-group-sm">
                                                <button
                                                    className="btn btn-outline-primary"
                                                    onClick={() => handleTest(conn.connection_id)}
                                                    disabled={testResult?.loading && testResult?.connectionId === conn.connection_id}
                                                    title="Test Connection"
                                                >
                                                    {testResult?.loading && testResult?.connectionId === conn.connection_id ? (
                                                        <span className="spinner-border spinner-border-sm"></span>
                                                    ) : (
                                                        <i className="fas fa-plug"></i>
                                                    )}
                                                </button>
                                                <button
                                                    className="btn btn-outline-success"
                                                    onClick={() => handleSync(conn.connection_id)}
                                                    disabled={syncing === conn.connection_id}
                                                    title="Sync Catalog"
                                                >
                                                    {syncing === conn.connection_id ? (
                                                        <span className="spinner-border spinner-border-sm"></span>
                                                    ) : (
                                                        <i className="fas fa-sync"></i>
                                                    )}
                                                </button>
                                                <button
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => handleEdit(conn)}
                                                    title="Edit"
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button
                                                    className="btn btn-outline-danger"
                                                    onClick={() => handleDelete(conn.connection_id)}
                                                    title="Delete"
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title">
                                    <i className={`fas ${modalMode === 'create' ? 'fa-plus' : 'fa-edit'} me-2`}></i>
                                    {modalMode === 'create' ? 'New MDM Connection' : 'Edit Connection'}
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Connection Name *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.connection_name}
                                                onChange={(e) => setFormData({ ...formData, connection_name: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Connection Type *</label>
                                            <select
                                                className="form-select"
                                                value={formData.connection_type}
                                                onChange={(e) => setFormData({ ...formData, connection_type: e.target.value })}
                                                disabled={modalMode === 'edit'}
                                            >
                                                {availableTypes.map(type => (
                                                    <option key={type} value={type}>{type}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label">Description</label>
                                            <textarea
                                                className="form-control"
                                                rows="2"
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            />
                                        </div>

                                        {formData.connection_type !== 'MANUAL' && (
                                            <>
                                                <div className="col-md-8">
                                                    <label className="form-label">Base URL</label>
                                                    <input
                                                        type="url"
                                                        className="form-control"
                                                        value={formData.base_url}
                                                        onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                                                        placeholder="https://your-mdm-instance.com"
                                                    />
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-label">API Version</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={formData.api_version}
                                                        onChange={(e) => setFormData({ ...formData, api_version: e.target.value })}
                                                        placeholder="v1"
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label">Authentication Type</label>
                                                    <select
                                                        className="form-select"
                                                        value={formData.auth_type}
                                                        onChange={(e) => setFormData({ ...formData, auth_type: e.target.value })}
                                                    >
                                                        <option value="API_KEY">API Key</option>
                                                        <option value="OAUTH2">OAuth 2.0</option>
                                                        <option value="BASIC">Basic Auth</option>
                                                        <option value="TOKEN">Bearer Token</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label">Vault Path</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={formData.vault_path}
                                                        onChange={(e) => setFormData({ ...formData, vault_path: e.target.value })}
                                                        placeholder="secret/dama/mdm/connection_name"
                                                    />
                                                    <small className="text-muted">Path to credentials in HashiCorp Vault</small>
                                                </div>
                                            </>
                                        )}

                                        <div className="col-md-6">
                                            <div className="form-check form-switch mt-4">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="autoSync"
                                                    checked={formData.auto_sync_enabled}
                                                    onChange={(e) => setFormData({ ...formData, auto_sync_enabled: e.target.checked })}
                                                />
                                                <label className="form-check-label" htmlFor="autoSync">
                                                    Enable Auto Sync
                                                </label>
                                            </div>
                                        </div>
                                        {formData.auto_sync_enabled && (
                                            <div className="col-md-6">
                                                <label className="form-label">Sync Schedule (Cron)</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={formData.sync_schedule}
                                                    onChange={(e) => setFormData({ ...formData, sync_schedule: e.target.value })}
                                                    placeholder="0 0 * * *"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        <i className={`fas ${modalMode === 'create' ? 'fa-plus' : 'fa-save'} me-2`}></i>
                                        {modalMode === 'create' ? 'Create Connection' : 'Save Changes'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProConnections;
